/**
 * useContextMenu Hook
 * Manages context menu state and positioning
 * Extracted from InventoryView to reduce file size and improve testability
 */

import { useState, useCallback } from 'react';

export interface SavedItem {
  id: number;
  [key: string]: any;
}

export interface MenuPosition {
  x: number;
  y: number;
}

export interface UseContextMenuReturn {
  // State
  contextItem: SavedItem | null;
  showContextMenu: boolean;
  menuPosition: MenuPosition;
  
  // Actions
  openContextMenu: (item: SavedItem, position: MenuPosition) => void;
  closeContextMenu: () => void;
  setContextItem: (item: SavedItem | null) => void;
}

/**
 * Custom hook for managing context menu state
 * Handles right-click menu for item operations
 * 
 * @returns Context menu state and actions
 */
export function useContextMenu(): UseContextMenuReturn {
  const [contextItem, setContextItem] = useState<SavedItem | null>(null);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({ x: 0, y: 0 });

  /**
   * Open context menu at specific position
   */
  const openContextMenu = useCallback((item: SavedItem, position: MenuPosition) => {
    setContextItem(item);
    setMenuPosition(position);
    setShowContextMenu(true);
  }, []);

  /**
   * Close context menu
   */
  const closeContextMenu = useCallback(() => {
    setShowContextMenu(false);
    // Don't clear contextItem immediately to allow for smooth animations
    setTimeout(() => {
      setContextItem(null);
    }, 200);
  }, []);

  return {
    // State
    contextItem,
    showContextMenu,
    menuPosition,
    
    // Actions
    openContextMenu,
    closeContextMenu,
    setContextItem,
  };
}
